import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { ISelectOption } from '@core/interfaces/select-option.interface';
import { AuthService } from '@core/services/auth.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseComponent } from '@shared/components/base.component';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { isAfter } from 'date-fns';
import { filter } from 'rxjs';
import { WindowRedirectService } from '../../../../../core/services/window-redirect.service';
import { IInvitationCreatePayload, TAdminInvitationRole } from '../../admin-user-group.interface';
import { IAdminInvitationDialogData } from './admin-invitation-dialog.interface';
import { AdminInvitationDialogStore } from './admin-invitation-dialog.store';

@Component({
  imports: [
    SideDialogComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslateModule,
    DotsSpinnerComponent,
    MatErrorComponent,
    DatePipe,
    HasPermissionDirective,
    IconComponent,
  ],
  providers: [AdminInvitationDialogStore],
  selector: 'app-admin-invitation-dialog',
  templateUrl: './admin-invitation-dialog.component.html',
})
export class AdminInvitationDialogComponent extends BaseComponent {
  public readonly form = new FormGroup({
    clientName: new FormControl<string>(''),
    email: new FormControl<string>('', [Validators.required, Validators.email]),
    link: new FormControl<string | null>(null),
    message: new FormControl<string>(''),
    role: new FormControl<TAdminInvitationRole | null>(null, [Validators.required]),
  });

  public readonly invitationExpired = computed(() => {
    const { invitation } = this.data;

    return invitation?.expire && isAfter(new Date(), this.dateUtils.parseDateTimeFromApi(invitation.expire));
  });

  public readonly invitationExpiryDate = computed(() => {
    return this.data.invitation?.expire ? this.dateUtils.parseDateTimeFromApi(this.data.invitation.expire) : null;
  });

  public readonly hasMessageField = signal<boolean>(false);
  public readonly hasClientNameField = signal<boolean>(false);
  public readonly hasRoleField = signal<boolean>(false);

  public readonly isSendRequest = signal<boolean>(false);

  public readonly invitationPermissionSuffix = '_user_invitation_create' as const;
  public readonly invitationLinkBase = `${WindowRedirectService.getBaseUrl()}/invitations/` as const;

  public readonly data = inject<IAdminInvitationDialogData>(MAT_DIALOG_DATA);
  public readonly store = inject(AdminInvitationDialogStore);
  private readonly dialogRef = inject(MatDialogRef<AdminInvitationDialogComponent>);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly dialog = inject(MatDialog);
  private readonly authService = inject(AuthService);

  public readonly roleOptions: ISelectOption<TAdminInvitationRole>[] = [
    {
      label: this.translate.instant('pages.admin.userGroup.tabs.invitations.roles.support'),
      value: this.ERoles.support,
    },
    {
      label: this.translate.instant('pages.admin.userGroup.tabs.invitations.roles.campaign'),
      value: this.ERoles.campaign,
    },
  ];

  constructor() {
    super();

    this.initialize();
    this.setupEffects();
  }

  public onCloseClick(): void {
    this.dialogRef.close();
  }

  public onSubmit(): void {
    if (this.form.invalid || this.data.invitation?.createdUserId) {
      this.form.markAllAsTouched();

      return;
    }

    this.isSendRequest.set(true);

    const formValue = this.form.value;
    const formRawValue = this.form.getRawValue();
    const payload: IInvitationCreatePayload = {
      clientName: formRawValue.role === this.ERoles.campaign ? formRawValue.clientName || undefined : undefined,
      email: formValue.email!,
      message: formValue.message || undefined,
    };

    this.store.sendInvitation({
      payload,
      role: this.data.invitation?.role || formRawValue.role!,
    });
  }

  public onRevokeClick(): void {
    if (!this.data.invitation?.id || !this.data.invitation?.role) {
      return;
    }

    if (this.invitationExpired()) {
      this.isSendRequest.set(false);
      this.store.revokeInvitation({
        id: this.data.invitation!.id,
        role: this.data.invitation!.role!,
      });

      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('shared.cancel'),
        confirmText: this.translate.instant('shared.delete'),
        message: this.translate.instant(
          'pages.admin.userGroup.tabs.invitations.dialog.revokeConfirmationDialog.message',
        ),
        title: this.translate.instant('pages.admin.userGroup.tabs.invitations.dialog.revokeConfirmationDialog.title'),
      },
      width: '400px',
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.isSendRequest.set(false);
        this.store.revokeInvitation({
          id: this.data.invitation!.id,
          role: this.data.invitation!.role!,
        });
      });
  }

  public onRoleChange(role: TAdminInvitationRole): void {
    this.hasMessageField.set(role !== this.ERoles.campaign && role !== this.ERoles.support);
    this.hasClientNameField.set(role === this.ERoles.campaign);
    this.hasRoleField.set(role === this.ERoles.campaign || role === this.ERoles.support);

    if (this.hasRoleField()) {
      this.form.get('role')?.addValidators([Validators.required]);
    } else {
      this.form.get('role')?.removeValidators([Validators.required]);
    }
  }

  public onCopyLinkClick(displaySnackbar = true): void {
    const link = this.form.get('link')?.value;

    if (!link) {
      return;
    }

    navigator.clipboard.writeText(link);

    if (displaySnackbar) {
      this.snackbarService.openSuccess(this.translate.instant('pages.campaignDetails.export.linkCopied'));
    }
  }

  private setupEffects(): void {
    effect(() => {
      const loading = this.store.loading$();
      const error = this.store.error$();

      if (loading || (!this.store.sent$() && !this.store.revoked$())) {
        return;
      }

      if (error) {
        this.snackbarService.openError(error);
      }

      this.authService.refreshUserGroups();

      if (this.store.invitation$() && this.store.sent$() && !this.store.invitation$()?.emailSent) {
        this.form.get('link')?.setValue(`${this.invitationLinkBase}${this.store.invitation$()?.id}`);
        this.form.get('role')?.disable();
        this.onCopyLinkClick(false);
        this.data.emailNotSent.set(true);
        this.snackbarService.openError(
          this.translate.instant('pages.admin.userGroup.tabs.invitations.dialog.sentErrorMessage'),
        );
      }

      if (this.store.sent$() && this.store.invitation$()?.emailSent) {
        this.snackbarService.openSuccess(
          this.translate.instant('pages.admin.userGroup.tabs.invitations.dialog.sentMessage', {
            email: this.form.getRawValue().email,
          }),
        );
        this.dialogRef.close(this.store.invitation$());
      }

      if (this.store.revoked$()) {
        this.snackbarService.openSuccess(
          this.translate.instant('pages.admin.userGroup.tabs.invitations.dialog.revokedMessage', {
            email: this.form.getRawValue().email,
          }),
        );
        this.dialogRef.close(this.data.invitation);
      }
    });
  }

  private initialize(): void {
    const { invitation, userGroups, selectedRole } = this.data;

    this.form.get('link')?.disable();

    if ((invitation?.role ?? selectedRole) === this.ERoles.campaign) {
      this.form.get('clientName')?.setValidators([Validators.required]);
      this.hasClientNameField.set(true);
    }

    if (!invitation) {
      this.hasRoleField.set(true);

      if (selectedRole) {
        this.form.get('role')?.setValue(selectedRole);
      }

      return;
    }

    this.store.patchState({ invitation });
    this.form.patchValue({
      clientName: userGroups.find((ug) => ug.id === invitation.userGroupId)?.name || '',
      email: invitation.email,
      link: `${this.invitationLinkBase}${invitation.id}`,
      message: invitation.message || '',
      role: invitation.role,
    });

    this.onRoleChange(invitation.role!);

    this.form.get('clientName')?.disable();
    this.form.get('role')?.disable();

    if (invitation.createdUserId || !this.hasRoleField()) {
      this.form.disable();
    }
  }
}
